import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Check, Copy, Loader2, Sparkles } from 'lucide-react'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/hooks/use-toast'
import { describeApiError } from '@/lib/apiError'
import type { KitCopy, KitLanguage, MarketingKit } from '@/types/marketingProactive'

const FIELDS: { key: keyof KitCopy; label: string; rows?: number; hint?: string }[] = [
  { key: 'headline', label: 'Headline', hint: 'On the post, story and poster.' },
  { key: 'subhead', label: 'Line under the headline' },
  { key: 'offer_line', label: 'Offer line' },
  { key: 'story_text', label: 'Story text', hint: 'The big words on the story.' },
  { key: 'caption', label: 'Instagram / Facebook caption', rows: 5 },
  { key: 'whatsapp', label: 'WhatsApp message', rows: 6, hint: '{{first_name}} and {{promo_code}} are filled in per guest.' },
  {
    key: 'wa_offer_text',
    label: 'WhatsApp template line',
    hint: 'Business WhatsApp sends an approved template: “<restaurant> has something for you: <this line>.”',
  },
  { key: 'email_subject', label: 'Email subject' },
  { key: 'email_body', label: 'Email', rows: 6 },
]

function sourceLabel(src: string): string {
  if (!src || src === 'template') return 'Written from a template'
  if (src.includes('haiku')) return 'Written by Claude Haiku'
  if (src.includes('sonnet')) return 'Written by Claude Sonnet'
  if (src.includes('opus')) return 'Written by Claude Opus'
  return `Written by ${src}`
}

function CopyButton({ text }: { text: string }) {
  const [done, setDone] = useState(false)
  return (
    <Button
      type="button"
      size="sm"
      variant="ghost"
      className="h-7 px-2"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text)
          setDone(true)
          setTimeout(() => setDone(false), 1500)
        } catch {
          /* clipboard blocked — the text is selectable */
        }
      }}
      aria-label="Copy"
    >
      {done ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
    </Button>
  )
}

/** The kit's words — editable any time, rewritable by Claude. */
export function KitWords({ kit }: { kit: MarketingKit }) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [copy, setCopy] = useState<KitCopy>(kit.copy)
  const [language, setLanguage] = useState<KitLanguage>(kit.language)
  useEffect(() => setCopy(kit.copy), [kit.copy])
  useEffect(() => setLanguage(kit.language), [kit.language])
  const dirty = JSON.stringify(copy) !== JSON.stringify(kit.copy)

  const onKit = (res: { data?: MarketingKit; message?: string }) => {
    queryClient.setQueryData(['marketing', 'kit', kit.id], res)
  }
  const save = useMutation({
    mutationFn: () => apiClient.updateMarketingKit(kit.id, { copy }),
    onSuccess: (res) => {
      onKit(res)
      toast({ title: 'Words saved' })
    },
    onError: (err) => toast({ title: 'Could not save', description: describeApiError(err).message, variant: 'destructive' }),
  })
  const rewrite = useMutation({
    mutationFn: () => apiClient.rewriteMarketingKitCopy(kit.id, language),
    onSuccess: (res) => {
      onKit(res)
      toast({ title: res.message || 'New copy written' })
    },
    onError: (err) => toast({ title: 'Could not rewrite', description: describeApiError(err).message, variant: 'destructive' }),
  })

  return (
    <Card>
      <CardHeader className="space-y-3 pb-2">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base">Words</CardTitle>
          <span className="text-xs text-muted-foreground">{sourceLabel(kit.copy_source)}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={language} onValueChange={(v) => setLanguage(v as KitLanguage)}>
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="english">English</SelectItem>
              <SelectItem value="roman_urdu">Roman Urdu mix</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            disabled={rewrite.isPending || dirty}
            title={dirty ? 'Save or undo your edits first' : undefined}
            onClick={() => rewrite.mutate()}
          >
            {rewrite.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
            {rewrite.isPending ? 'Writing…' : 'Rewrite'}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {FIELDS.map((f) => (
          <div key={f.key} className="space-y-1.5">
            <div className="flex items-center justify-between gap-2">
              <Label htmlFor={`copy-${f.key}`}>{f.label}</Label>
              <CopyButton text={copy[f.key] ?? ''} />
            </div>
            {f.rows ? (
              <Textarea
                id={`copy-${f.key}`}
                rows={f.rows}
                value={copy[f.key] ?? ''}
                onChange={(e) => setCopy({ ...copy, [f.key]: e.target.value })}
              />
            ) : (
              <Input id={`copy-${f.key}`} value={copy[f.key] ?? ''} onChange={(e) => setCopy({ ...copy, [f.key]: e.target.value })} />
            )}
            {f.hint && <p className="text-xs text-muted-foreground">{f.hint}</p>}
          </div>
        ))}
        {dirty && (
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setCopy(kit.copy)}>
              Undo
            </Button>
            <Button disabled={save.isPending} onClick={() => save.mutate()}>
              Save words
            </Button>
          </div>
        )}
        {kit.status === 'launched' && kit.campaign && !kit.campaign.sent_at && (
          <p className="text-xs text-muted-foreground">
            The campaign took a copy of the words at launch — edit its message in Campaigns before sending.
          </p>
        )}
      </CardContent>
    </Card>
  )
}
